import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiAlertCircle, FiClock, FiDollarSign, FiFileText, FiSearch, FiExternalLink, FiDownload, FiRefreshCw } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { formatDate as fmtDate } from '../../utils/formatters';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import useReportMemory from '../../hooks/useReportMemory';
import PageLayout from '../../components/PageLayout';
import { TRAN_TYPE_ROUTES, TRAN_TYPE_LABELS, navigateToTransaction } from '../../utils/navigationHelper';
import { formatAmount } from '../../utils/documentNumbering';

const formatDate = (v) => fmtDate(v) || '-';

const daysBetween = (iso) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (isNaN(t)) return null;
  return Math.floor((Date.now() - t) / (1000 * 60 * 60 * 24));
};

const DraftReport = () => {
  const navigate = useNavigate();
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  // Backward memory: restore filters + loaded rows on Back (0 re-reads).
  const [savedMem, saveMemory] = useReportMemory('draft-report');

  const [drafts, setDrafts] = useState(() => savedMem?.drafts ?? []);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState(() => savedMem?.searchQuery ?? '');
  const [tranTypeFilter, setTranTypeFilter] = useState(() => savedMem?.tranTypeFilter ?? 'all');
  const [createdByFilter, setCreatedByFilter] = useState(() => savedMem?.createdByFilter ?? 'all');
  const [ageFilter, setAgeFilter] = useState(() => savedMem?.ageFilter ?? 'all'); // 'all' | 'today' | 'week' | 'old'
  const [sortField, setSortField] = useState(() => savedMem?.sortField ?? 'age');
  const [sortDir, setSortDir] = useState(() => savedMem?.sortDir ?? 'desc');

  const loadDrafts = useCallback(async () => {
    if (!activeCompany?.id) return;
    setLoading(true);
    try {
      const tref = collection(db, 'companies', activeCompany.id, 'transactions');
      const conds = [where('status', '==', 'Draft')];
      if (activeFinancialYear?.id) conds.push(where('financialYearId', '==', activeFinancialYear.id));
      const snap = await getDocs(query(tref, ...conds));
      let rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Branch scope
      if (activeBranch && activeBranch.id !== 'all') {
        rows = rows.filter(r => r.branchId === activeBranch.id);
      }
      // Exclude POS sales — they auto-post and don't sit as actionable drafts
      rows = rows.filter(r => r.tranTypeCode !== 'PS');
      setDrafts(rows);
    } catch (err) {
      console.error('Error loading drafts:', err);
    } finally {
      setLoading(false);
    }
  }, [activeCompany, activeFinancialYear, activeBranch]);

  const loadUsers = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const snap = await getDocs(collection(db, 'companies', activeCompany.id, 'users'));
      setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error('Error loading users:', err); }
  }, [activeCompany]);

  // Backward memory: on Back (snapshot present) skip ONLY the first drafts
  // auto-load so restored rows are reused with 0 re-reads; a later company/
  // branch/FY change still re-fetches. Users are lookup data — reload normally.
  const skipInitialLoad = useRef(!!savedMem);
  useEffect(() => {
    if (skipInitialLoad.current) { skipInitialLoad.current = false; return; }
    loadDrafts();
  }, [loadDrafts]);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  // Backward memory: keep the snapshot in sync (filters are display-only over the
  // already-loaded drafts), so Back restores this exact view with 0 re-reads.
  useEffect(() => {
    if (loading) return;
    saveMemory({
      drafts,
      searchQuery, tranTypeFilter, createdByFilter, ageFilter, sortField, sortDir,
    });
  }, [loading, drafts, searchQuery, tranTypeFilter, createdByFilter, ageFilter,
      sortField, sortDir, saveMemory]);

  const userMap = useMemo(() => {
    const m = {};
    users.forEach(u => { m[u.id] = u.displayName || u.fullName || u.email || u.id; });
    return m;
  }, [users]);

  // Available tran types from current drafts (drives filter dropdown)
  const availableTypes = useMemo(() => {
    const set = new Set(drafts.map(d => d.tranTypeCode).filter(Boolean));
    return [...set].sort();
  }, [drafts]);

  const availableUsers = useMemo(() => {
    const set = new Set(drafts.map(d => d.createdBy).filter(Boolean));
    return [...set].map(uid => ({ uid, name: userMap[uid] || uid })).sort((a, b) => a.name.localeCompare(b.name));
  }, [drafts, userMap]);

  // Apply filters + sort
  const filtered = useMemo(() => {
    let rows = drafts.slice();

    if (tranTypeFilter !== 'all') rows = rows.filter(r => r.tranTypeCode === tranTypeFilter);
    if (createdByFilter !== 'all') rows = rows.filter(r => r.createdBy === createdByFilter);

    if (ageFilter !== 'all') {
      rows = rows.filter(r => {
        const age = daysBetween(r.createdAt) ?? 0;
        if (ageFilter === 'today') return age < 1;
        if (ageFilter === 'week') return age >= 1 && age <= 7;
        if (ageFilter === 'old') return age > 7;
        return true;
      });
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      rows = rows.filter(r =>
        (r.docNumber || '').toLowerCase().includes(q) ||
        (r.partyName || '').toLowerCase().includes(q) ||
        (r.notes || '').toLowerCase().includes(q)
      );
    }

    rows.sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      let av, bv;
      switch (sortField) {
        case 'docNumber': av = a.docNumber || ''; bv = b.docNumber || ''; break;
        case 'tranType': av = a.tranTypeCode || ''; bv = b.tranTypeCode || ''; break;
        case 'docDate': av = a.docDate || ''; bv = b.docDate || ''; break;
        case 'partyName': av = (a.partyName || '').toLowerCase(); bv = (b.partyName || '').toLowerCase(); break;
        case 'amount': av = Number(a.totalAmount) || 0; bv = Number(b.totalAmount) || 0; break;
        case 'age':
        default: av = daysBetween(a.createdAt) ?? 0; bv = daysBetween(b.createdAt) ?? 0; break;
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return rows;
  }, [drafts, tranTypeFilter, createdByFilter, ageFilter, searchQuery, sortField, sortDir]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir(field === 'age' || field === 'amount' ? 'desc' : 'asc'); }
  };

  // KPIs
  const kpi = useMemo(() => {
    const total = drafts.length;
    const totalValue = drafts.reduce((s, d) => s + (Number(d.totalAmount) || 0), 0);
    const ages = drafts.map(d => daysBetween(d.createdAt) ?? 0);
    const oldest = ages.length ? Math.max(...ages) : 0;
    const todayCount = ages.filter(a => a < 1).length;
    const weekCount = ages.filter(a => a >= 1 && a <= 7).length;
    const oldCount = ages.filter(a => a > 7).length;
    return { total, totalValue, oldest, todayCount, weekCount, oldCount };
  }, [drafts]);

  const handleExport = () => {
    if (filtered.length === 0) return;
    let csv = 'Doc Number,Type,Doc Date,Created,Created By,Party,Amount,Days as Draft\n';
    filtered.forEach(r => {
      const age = daysBetween(r.createdAt);
      const type = TRAN_TYPE_LABELS[r.tranTypeCode] || r.tranTypeCode || '';
      csv += `"${r.docNumber || ''}","${type}","${formatDate(r.docDate)}","${formatDate(r.createdAt)}","${(userMap[r.createdBy] || '').replace(/"/g, '""')}","${(r.partyName || '').replace(/"/g, '""')}",${Number(r.totalAmount) || 0},${age ?? ''}\n`;
    });
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Draft_Report_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <PageLayout pageTitle="Draft Report" breadcrumb="Reports / Main Reports / Draft Report">
      {/* KPI strip */}
      <KpiGrid>
        <KpiCard $accent={colors.warning.main}>
          <KpiLabel><FiFileText /> Total Drafts</KpiLabel>
          <KpiValue>{kpi.total}</KpiValue>
        </KpiCard>
        <KpiCard $accent={colors.primary.main}>
          <KpiLabel><FiDollarSign /> Draft Value</KpiLabel>
          <KpiValue>{formatAmount(Math.round(kpi.totalValue))}</KpiValue>
        </KpiCard>
        <KpiCard $accent={colors.info.main}>
          <KpiLabel><FiClock /> Created Today</KpiLabel>
          <KpiValue>{kpi.todayCount}</KpiValue>
        </KpiCard>
        <KpiCard $accent={colors.warning.main}>
          <KpiLabel><FiClock /> Within 7 Days</KpiLabel>
          <KpiValue>{kpi.weekCount}</KpiValue>
        </KpiCard>
        <KpiCard $accent={colors.error.main}>
          <KpiLabel><FiAlertCircle /> Older than 7 Days</KpiLabel>
          <KpiValue>{kpi.oldCount}</KpiValue>
        </KpiCard>
        <KpiCard $accent={colors.error.main}>
          <KpiLabel><FiAlertCircle /> Oldest (days)</KpiLabel>
          <KpiValue>{kpi.oldest}</KpiValue>
        </KpiCard>
      </KpiGrid>

      {/* Filters */}
      <FiltersBar>
        <SearchBox>
          <FiSearch />
          <input type="text" placeholder="Search doc#, party, notes..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </SearchBox>
        <FilterSelect value={tranTypeFilter} onChange={e => setTranTypeFilter(e.target.value)}>
          <option value="all">All Document Types</option>
          {availableTypes.map(c => (
            <option key={c} value={c}>{TRAN_TYPE_LABELS[c] || c}</option>
          ))}
        </FilterSelect>
        <FilterSelect value={createdByFilter} onChange={e => setCreatedByFilter(e.target.value)}>
          <option value="all">All Users</option>
          {availableUsers.map(u => (
            <option key={u.uid} value={u.uid}>{u.name}</option>
          ))}
        </FilterSelect>
        <FilterSelect value={ageFilter} onChange={e => setAgeFilter(e.target.value)}>
          <option value="all">Any Age</option>
          <option value="today">Today only</option>
          <option value="week">Within 7 days</option>
          <option value="old">Older than 7 days</option>
        </FilterSelect>
        <Btn onClick={loadDrafts} title="Refresh"><FiRefreshCw /></Btn>
        <Btn onClick={handleExport} disabled={filtered.length === 0}><FiDownload /> Export</Btn>
      </FiltersBar>

      <ContentCard>
        <SubHeader>
          <span><strong>{filtered.length}</strong> draft{filtered.length === 1 ? '' : 's'} shown</span>
        </SubHeader>

        {loading ? (
          <Empty>Loading drafts...</Empty>
        ) : filtered.length === 0 ? (
          <Empty>
            {drafts.length === 0
              ? '✓ No drafts in the active financial year. Everything is posted or voided.'
              : 'No drafts match the current filters.'}
          </Empty>
        ) : (
          <Table>
            <thead>
              <tr>
                <SortableTh onClick={() => handleSort('docNumber')} $active={sortField === 'docNumber'}>Doc #</SortableTh>
                <SortableTh onClick={() => handleSort('tranType')} $active={sortField === 'tranType'}>Type</SortableTh>
                <SortableTh onClick={() => handleSort('docDate')} $active={sortField === 'docDate'}>Doc Date</SortableTh>
                <Th>Created</Th>
                <Th>Created By</Th>
                <SortableTh onClick={() => handleSort('partyName')} $active={sortField === 'partyName'}>Party</SortableTh>
                <SortableTh onClick={() => handleSort('amount')} $active={sortField === 'amount'} style={{ textAlign: 'right' }}>Amount</SortableTh>
                <SortableTh onClick={() => handleSort('age')} $active={sortField === 'age'} style={{ textAlign: 'right' }}>Days as Draft</SortableTh>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const age = daysBetween(r.createdAt) ?? 0;
                const ageColor = age <= 3 ? colors.success.main : age <= 14 ? colors.warning.main : colors.error.main;
                const route = TRAN_TYPE_ROUTES[r.tranTypeCode];
                return (
                  <Tr key={r.id}>
                    <Td>
                      {route ? (
                        <DocLink onClick={() => navigateToTransaction(navigate, r.tranTypeCode, r.id)} title="Open document">
                          <strong>{r.docNumber || '-'}</strong> <FiExternalLink size={11} />
                        </DocLink>
                      ) : (
                        <strong>{r.docNumber || '-'}</strong>
                      )}
                    </Td>
                    <Td><TypeBadge>{TRAN_TYPE_LABELS[r.tranTypeCode] || r.tranTypeCode || '-'}</TypeBadge></Td>
                    <Td>{formatDate(r.docDate)}</Td>
                    <Td>{formatDate(r.createdAt)}</Td>
                    <Td>{userMap[r.createdBy] || '-'}</Td>
                    <Td>{r.partyName || '-'}</Td>
                    <Td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{formatAmount(Number(r.totalAmount) || 0)}</Td>
                    <Td style={{ textAlign: 'right' }}>
                      <AgePill style={{ background: `${ageColor}20`, color: ageColor }}>
                        {age}d
                      </AgePill>
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </ContentCard>
    </PageLayout>
  );
};

export default DraftReport;

// ─── Styled ───────────────────────────────────────────────────────────────────
const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;
  margin-bottom: 16px;
  @media (max-width: 1100px) { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 600px)  { grid-template-columns: repeat(2, 1fr); }
`;
const KpiCard = styled.div`
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-left: 3px solid ${p => p.$accent || colors.primary.main};
  border-radius: 8px;
  padding: 12px 14px;
`;
const KpiLabel = styled.div`
  font-size: 11px;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  display: flex; align-items: center; gap: 6px;
  margin-bottom: 6px;
`;
const KpiValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  font-family: 'SF Mono', 'Consolas', monospace;
  color: ${colors.text.primary};
`;

const FiltersBar = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 14px;
  align-items: center;
`;
const SearchBox = styled.div`
  display: flex; align-items: center; gap: 8px;
  background: white; border: 1px solid ${colors.neutral[300]}; border-radius: 8px;
  padding: 6px 12px;
  flex: 1; min-width: 240px; max-width: 400px;
  svg { color: ${colors.text.secondary}; }
  input { border: none; outline: none; flex: 1; font-size: 13px; }
`;
const FilterSelect = styled.select`
  padding: 7px 10px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: 8px;
  background: white;
  font-size: 13px;
  cursor: pointer;
`;
const Btn = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 12px;
  border: 1px solid ${colors.neutral[300]};
  background: white; color: ${colors.text.primary};
  border-radius: 8px; cursor: pointer; font-size: 13px;
  &:hover:not(:disabled) { border-color: ${colors.primary.main}; color: ${colors.primary.main}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const ContentCard = styled.div`
  background: white; border: 1px solid ${colors.neutral[200]}; border-radius: 10px; padding: 14px 18px;
`;
const SubHeader = styled.div`
  font-size: 13px; color: ${colors.text.secondary}; margin-bottom: 10px;
`;
const Empty = styled.div`
  text-align: center; padding: 40px 16px; color: ${colors.text.secondary}; font-size: 14px;
`;

const Table = styled.table`width: 100%; border-collapse: collapse; font-size: 13px;`;
const Th = styled.th`
  text-align: left; padding: 10px 12px;
  background: ${colors.neutral[50]};
  border-bottom: 2px solid ${colors.neutral[200]};
  font-size: 11px; text-transform: uppercase; letter-spacing: 0.4px;
  color: ${colors.text.secondary}; font-weight: 600;
`;
const SortableTh = styled(Th)`
  cursor: pointer; user-select: none;
  color: ${p => p.$active ? colors.primary.main : colors.text.secondary};
  &:hover { color: ${colors.primary.main}; }
`;
const Tr = styled.tr`
  &:hover { background: ${colors.neutral[50]}; }
  &:not(:last-child) td { border-bottom: 1px solid ${colors.neutral[100]}; }
`;
const Td = styled.td`padding: 10px 12px; color: ${colors.text.primary};`;
const DocLink = styled.button`
  background: none; border: none; padding: 0; cursor: pointer;
  color: ${colors.primary.main}; display: inline-flex; align-items: center; gap: 4px;
  font-size: inherit; font-family: inherit;
  &:hover { text-decoration: underline; }
`;
const TypeBadge = styled.span`
  display: inline-block;
  padding: 2px 8px; border-radius: 10px;
  background: ${colors.neutral[100]}; color: ${colors.text.secondary};
  font-size: 11px; font-weight: 600;
`;
const AgePill = styled.span`
  display: inline-block;
  padding: 2px 10px; border-radius: 12px;
  font-size: 12px; font-weight: 600; font-family: 'SF Mono', 'Consolas', monospace;
`;
